import { useState } from 'react';
import { clsx } from 'clsx';
import { useMasterDocumentTranslation } from './i18n';
import type { FactBoxProps, FactBoxSectionConfig } from './MasterDocument.types';

// ─── Chevron icon ──────────────────────────────────────────

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={clsx('w-3.5 h-3.5 transition-transform duration-200', open ? 'rotate-90' : 'rotate-0')}
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
    </svg>
  );
}


// ─── FactBox Widget ────────────────────────────────────────

function FactBoxWidget({ section }: { section: FactBoxSectionConfig }) {
  const [open, setOpen] = useState(section.defaultOpen);

  return (
    <div
      className={clsx(
        // Tarjeta del canon, la MISMA que envuelve las tablas y la del detalle:
        // `rounded-xl` + borde de 1px en `border-primary`, sin sombra.
        //
        // Antes era `rounded-[10px]` SIN borde y con una sombra doble muy tenue
        // (`0 1px 3px rgba(60,64,67,.15)`). Sobre el blanco de la tarjeta del detalle
        // esa sombra no dibuja un contorno: el widget flotaba sin bordes claros y no
        // se distinguía dónde acababa uno y empezaba el siguiente. Un borde sólido a
        // 1px es lo que separa dos superficies del mismo color; una sombra sirve para
        // levantar algo por encima de un fondo distinto, que no es este caso.
        'bg-bg-primary rounded-xl overflow-hidden',
        'border border-border-primary dark:border-dark-border-primary',
        'dark:bg-dark-bg-primary',
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          // 14px/600, el mismo peso y tamaño que la cabecera de una tabla: los 12px
          // de antes hacían que el título del widget se leyera como una nota al pie.
          // `px-4 py-3` iguala el ritmo horizontal con el resto de las cajas.
          'flex items-center justify-between w-full px-4 py-3 text-left',
          // Es un desplegable: tiene que anunciarse como pulsable al pasar por encima.
          'hover:bg-background-secondary/70 dark:hover:bg-dark-bg-secondary/50 transition-colors duration-150',
          'focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-primary-400 dark:focus:ring-dark-border-custom',
        )}
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-content-primary dark:text-dark-content-primary truncate">
            {section.label}
          </span>
          {section.badge}
        </div>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div className="border-t border-border-secondary dark:border-dark-border-primary px-4 py-3">
          {section.children}
        </div>
      )}
    </div>
  );
}

// ─── FactBox Panel Content ─────────────────────────────────

function FactBoxContent({ sections, grid }: { sections: FactBoxSectionConfig[]; grid?: boolean }) {
  return (
    <div
      className={clsx(
        // La separación vive en el contenedor (`gap-3`) y no en un `mb-2` dentro de
        // cada tarjeta: así la última no arrastra un margen sobrante al final del riel.
        grid
          // Por debajo de `xl` los widgets se reparten en rejilla en vez de apilarse:
          // en una tablet en horizontal tres columnas de una sola tarjeta desperdiciaban
          // el ancho y estiraban la página a lo tonto.
          ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-start'
          : 'flex flex-col gap-3',
      )}
    >
      {sections.map((section) => (
        <FactBoxWidget key={section.key} section={section} />
      ))}
    </div>
  );
}

// ─── FactBox ───────────────────────────────────────────────

/**
 * Panel de información contextual (FactBox) del documento maestro.
 *
 * Se adapta a tres breakpoints:
 * - **xl+**: barra lateral estática a la derecha del formulario.
 * - **md–lg**: panel deslizante desde la derecha (drawer), controlado por `open`/`onClose`.
 * - **xs–sm**: hoja inferior deslizante (bottom sheet), controlada por `open`/`onClose`.
 *
 * Acepta un arreglo de `FactBoxSectionConfig`, cada una con su propia clave,
 * etiqueta, insignia opcional y nodo hijo. Cada sección se renderiza como un
 * widget colapsable independiente (acordeón).
 *
 * @see docs/colors.md - Sistema de tokens de color
 * @see MasterDocument.types - FactBoxProps, FactBoxSectionConfig
 *
 * @example
 * ```tsx
 * <FactBox
 *   sections={[
 *     { key: 'totals', label: 'Totales', defaultOpen: true, children: <FactBoxTotals ... /> },
 *     { key: 'cfde',   label: 'DIAN',    defaultOpen: false, children: <FactBoxCfde ... /> },
 *   ]}
 *   open={drawerOpen}
 *   onClose={() => setDrawerOpen(false)}
 * />
 * ```
 */
export function FactBox({ sections, open: _open, onClose: _onClose, className }: FactBoxProps) {
  const { t } = useMasterDocumentTranslation();

  // `open`/`onClose` ya no gobiernan nada y se conservan sólo para no romper a los
  // hosts que los pasan. Ver la nota de abajo sobre por qué desapareció el cajón.
  void _open;
  void _onClose;

  return (
    <>
      {/* ────────────────────────────────────────────────────────────────────
          xl y más: riel lateral fijo, junto al contenido.
          ──────────────────────────────────────────────────────────────────── */}
      <aside
        className={clsx(
          // `top-0`, no `top-[52px]`: aquel 52 era el alto de la barra de acciones
          // que la cabecera apilada sustituyó, así que el riel quedaba anclado a
          // un elemento que ya no existe y arrancaba 52px por debajo de la tarjeta.
          'hidden xl:block w-[264px] shrink-0 sticky top-0 self-start',
          'max-h-[calc(100vh-100px)] overflow-y-auto',
          // Sin fondo propio: el panel principal dejó de pintar la bandeja gris
          // (ver `ContentLayout`) y mantenerla sólo aquí dejaba media pantalla
          // gris y media blanca.
          //
          // `pt-1` y no `pt-0`: el riel es un contenedor con `overflow-y-auto`, así que
          // un widget pegado a su borde superior queda con la esquina redondeada
          // RECORTADA por el propio scroller. Medido en dev el 2026-09-09: el primer
          // widget arrancaba en la misma `y` que el riel y se le comía la línea de
          // arriba. Cuatro píxeles bastan y no rompen la alineación.
          'pl-0 pr-4 md:pr-6 pt-1 pb-4',
          className,
        )}
        aria-label={t('master_document.factBox.aria')}
      >
        <FactBoxContent sections={sections} />
      </aside>

    </>
  );
}

/**
 * Los mismos widgets, pero POR DEBAJO DE `xl` y dentro del flujo del documento.
 *
 * Se renderiza al final de la columna de contenido —no como hermano de ella— y por
 * eso es un componente aparte: `ContentLayout` coloca su `factBox` como columna de
 * una fila flex, así que un bloque «para pantallas pequeñas» puesto ahí se pondría
 * AL LADO del documento en vez de debajo.
 *
 * Antes, por debajo de `xl`, esto eran dos superficies modales —un cajón lateral
 * entre `md` y `lg`, y una hoja inferior al 70% de alto en móvil—, las dos con
 * `fixed inset-0` y un velo `bg-black/30` sobre el documento. El efecto era el
 * contrario del que se busca: para consultar un total había que tapar y oscurecer
 * el documento que se está editando, y en el móvil la hoja se comía la pantalla.
 * Un panel auxiliar no secuestra la ventana.
 *
 * En el flujo, el contenido sigue mandando: los widgets son la última sección de la
 * página, se llega a ellos bajando, comparten el scroll del documento y en una
 * tablet se reparten en rejilla en vez de apilarse en una columna de una.
 */
export function FactBoxInline({ sections, className }: { sections: FactBoxSectionConfig[]; className?: string }) {
  const { t } = useMasterDocumentTranslation();
  // Desplegado de entrada: en una pantalla pequeña estos widgets son la última
  // sección del documento y nadie va a bajar hasta el final para descubrir que
  // además hay que abrirlos. El plegado sirve para APARTARLOS cuando estorban, no
  // para esconderlos por defecto.
  const [abierto, setAbierto] = useState(true);
  if (sections.length === 0) return null;
  return (
    <section
      className={clsx('xl:hidden w-full', className)}
      aria-label={t('master_document.factBox.aria')}
    >
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        className={clsx(
          'flex items-center gap-1.5 mb-3 rounded-md px-1 -mx-1 py-0.5',
          'text-sm font-bold text-content-primary dark:text-dark-content-primary',
          'hover:bg-background-secondary dark:hover:bg-dark-bg-secondary transition-colors duration-150',
          'focus:outline-hidden focus:ring-2 focus:ring-primary-400 dark:focus:ring-dark-border-custom',
        )}
      >
        <span>{t('master_document.factBox.info')}</span>
        <ChevronIcon open={abierto} />
      </button>
      {abierto && <FactBoxContent sections={sections} grid />}
    </section>
  );
}
